/**
 * AI CONTEXT
 * Purpose: collapsible teacher personal notes widget backed by user.personalNotes.
 * Open state persisted in userSettings.personalNotesOpen (closed by default).
 * Operations: add/complete/delete notes via Firebase user doc updates.
 * Used by: TeacherClassInfo panel.
 * Importance: teacher productivity/personal task memory; collapsed chrome stays
 *   compact so it cannot cover the standout save button.
 * Writes: transactional add/update via Firebase.addPersonalNote / updatePersonalNote.
 */
import { App, Collapse } from "antd"
import { orderBy, partition } from "lodash"
import { motion } from "motion/react"
import { useCallback, useMemo, useState } from "react"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import ComponentErrorBoundary from "#blocks/ComponentErrorBoundary"
import Flex from "#blocks/Flex"
import Firebase from "#classes/Firebase"
import QuickNoteListItem from "#components/views/AdminView/QuickNotes/QuickNoteListItem"
import QuickNotesInput from "#components/views/AdminView/QuickNotes/QuickNotesInput"
import { USER_SETTINGS } from "#constants/globalConstants"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { useUserContext } from "#contexts/UserContextProvider"
import { type QuickNotes as TeacherNotes } from "#customTypes/composedTypes/quickNotesTypes"
import { invariant } from "#customTypes/types"
import { ErrorType, formatError } from "#utils/textFormatters"

const PERSONAL_NOTES_PANEL_KEY = "personal-notes"

// If we ever want editable quick notes, try Typography.Text or TextInputToggle
const TeacherNotes = () => {
  const theme = useThemeContext()

  const { currentUser, getUserSetting, setUserSetting } = useUserContext()
  const notes = currentUser?.personalNotes

  const storedOpen = getUserSetting<boolean>(USER_SETTINGS.PERSONAL_NOTES_OPEN)
  const [openOverride, setOpenOverride] = useState<boolean | null>(null)
  const isOpen = openOverride ?? storedOpen ?? false

  const handleCollapseChange = useCallback(
    (keys: string | string[]) => {
      const open = Array.isArray(keys) ? keys : [keys]
      const next = open.includes(PERSONAL_NOTES_PANEL_KEY)
      setOpenOverride(next)
      setUserSetting(USER_SETTINGS.PERSONAL_NOTES_OPEN, next)
    },
    [setUserSetting]
  )

  const sortedQuickNotes = useMemo(() => {
    const activeNotes = orderBy(
      notes?.filter((note) => !note.deletedAt),
      ["createdAt"],
      ["desc"]
    )
    const [completed, pending] = partition(activeNotes, "completedAt")

    return { completed, pending }
  }, [notes])
  const { pending, completed } = sortedQuickNotes

  const { message } = App.useApp()

  const handleSubmit = async (note: string): Promise<string> => {
    invariant(currentUser != null, "Error finding current user")

    await Firebase.getInstance().addPersonalNote(currentUser.id, note)

    return currentUser.id
  }

  const updateNote = async (
    id: string,
    patch: Partial<
      Pick<
        TeacherNotes,
        "completedAt" | "completedBy" | "deletedAt" | "deletedBy"
      >
    >
  ) => {
    invariant(currentUser != null, "Error finding current user")

    try {
      await Firebase.getInstance().updatePersonalNote(currentUser.id, id, patch)
    } catch (error) {
      void message.error(
        `Failed to update note: ${formatError(error as ErrorType)}`
      )
    }
  }

  const handleDelete = (id: string) => {
    void updateNote(id, {
      deletedAt: new Date().toISOString(),
      deletedBy: currentUser?.id ?? null,
    })
  }

  const handleComplete = (id: string) => {
    void updateNote(id, {
      completedAt: new Date().toISOString(),
      completedBy: currentUser?.id ?? null,
    })
  }

  return (
    <div data-testid="teacher-personal-notes">
      <Collapse
        size="small"
        activeKey={isOpen ? [PERSONAL_NOTES_PANEL_KEY] : []}
        onChange={handleCollapseChange}
        items={[
          {
            key: PERSONAL_NOTES_PANEL_KEY,
            label: "Personal Notes",
            children: isOpen ? (
              <Flex
                col
                style={{
                  width: "100%",
                  maxHeight: 500,
                  overflow: "hidden",
                  border: "1px solid black",
                  borderRadius: 9,
                  padding: "30px 0 10px 30px",
                }}
                gap={10}
              >
                <Flex
                  col
                  grow
                  style={{
                    overflowY: "scroll",
                    paddingBottom: 50,
                    marginRight: 4,
                  }}
                  gap={10}
                >
                  {!pending.length && !completed.length && (
                    <AnimateInFlex
                      style={{ color: theme.gray[8] }}
                      animation={ANIMATION_BY_KEY.fade}
                      key="quick-notes-empty-state"
                    >
                      Add a note below!
                    </AnimateInFlex>
                  )}

                  <Flex col gap={6}>
                    {pending.map((note) => (
                      <ComponentErrorBoundary
                        key={note.id}
                        variant="line"
                        context="Note"
                      >
                        <QuickNoteListItem
                          item={note}
                          onDelete={handleDelete}
                          onComplete={handleComplete}
                        />
                      </ComponentErrorBoundary>
                    ))}
                  </Flex>
                  {!!completed.length && (
                    <>
                      <motion.h3
                        layoutId="quick-notes-completed-header"
                        key="quick-notes-completed-header"
                      >
                        Completed
                      </motion.h3>
                      <Flex col gap={6}>
                        {completed.map((note) => (
                          <ComponentErrorBoundary
                            key={note.id}
                            variant="line"
                            context="Note"
                          >
                            <QuickNoteListItem
                              item={note}
                              onDelete={handleDelete}
                              onComplete={handleComplete}
                            />
                          </ComponentErrorBoundary>
                        ))}
                      </Flex>
                    </>
                  )}
                </Flex>

                <QuickNotesInput onSubmit={handleSubmit} />
              </Flex>
            ) : null,
          },
        ]}
      />
    </div>
  )
}

export default TeacherNotes
